import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, RouterOutlet } from "@angular/router";
import { Location } from "@angular/common";
import { SkeletonModule } from "primeng/skeleton";
import { OpenItemsDataService } from "./services/open-items-data.service";
import { LotService } from "../../../../domains/lots/services/lot.service";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { AreaService } from "../../../../domains/areas/services/area.service";
import { JobIssueService } from "../../../../domains/jobIssues/services/job-issue-service";
import { JobService } from "../../../../domains/jobs/services/job.service";
import { AssertionIssuesService } from "../../../../domains/assertionIssues/services/assertion-issues.service";
import { JobIssueVendorService } from "../../../../domains/jobIssues/services/job-issue-vendor.service";
import { MediaService } from "../../../../domains/media/services/media.service";
import DataSyncService from "../../../../core/sync/data-sync/data-sync.service";
import { VendorService } from "../../../../domains/vendors/services/vendor-service";
import { ZoneService } from "../../../../domains/zones/services/zone.service";
import { CONSTANTS } from "../../../../constants";
import { UserVendorContactService } from "../../../../domains/vendors/services/user-vendor-contact.service";
import { ExtendedRouter } from "../../../../shared/services/extended-router.service";
import { ImageHydrationService } from "../../../../core/sync/image-sync/image-hydration.service";
import { LoggerService } from "../../../../shared/services/logger.service";

@Component({
  selector: "app-open-items-root",
  standalone: true,
  imports: [RouterOutlet, SkeletonModule],
  providers: [OpenItemsDataService],
  templateUrl: "./open-items-root.component.html",
  styleUrls: ["./open-items-root.component.scss"]
})
export class OpenItemsRootComponent implements OnInit {

  constructor(
    private route: ActivatedRoute,
    private router: ExtendedRouter,
    private location: Location,
    private jobService: JobService,
    private zonesService: ZoneService,
    private lotService: LotService,
    private areasService: AreaService,
    private imageHydrationService: ImageHydrationService,
    private jobIssueService: JobIssueService,
    private jobIssueVendorService: JobIssueVendorService,
    private assertionIssuesService: AssertionIssuesService,
    private mediaService: MediaService,
    private toastService: UiToastService,
    private vendorService: VendorService,
    private userVendorContactService: UserVendorContactService,
    private dataSyncService: DataSyncService,
    private logger: LoggerService,
    protected openItemsDataService: OpenItemsDataService
  ) { }

  async ngOnInit(): Promise<void> {
    const lotId = parseInt(this.route.snapshot.paramMap.get("lotId") ?? "0");
    const communityId = parseInt(this.route.snapshot.paramMap.get("communityId") ?? "0");
    const lot = await this.lotService.getLotById(lotId);

    const [zones, areas, assertionIssues, rawVendors] = await Promise.all([
      this.zonesService.getZonesByLotId(lotId),
      this.areasService.getAreasByLotId(lotId),
      this.assertionIssuesService.getAssertionIssuesForLot(lotId),
      this.vendorService.getVendorsForLotId(lotId)
    ]);

    if (!lot) {
      this.toastService.error("Error loading related lot data. Please try again.");
      this.goBack();
      return;
    }

    if (lot.externalFinancialCommunityId !== communityId) {
      this.toastService.error("Lot does not belong to the specified community. Please try again.");
      this.goBack();
      return;
    }

    const vendors = this.vendorService.processVendorData(rawVendors);

    this.openItemsDataService.lot.set(lot);
    this.openItemsDataService.financialCommunityId.set(communityId);
    this.openItemsDataService.zones.set(zones.filter(x => x.name.toLowerCase() !== CONSTANTS.ZONES.WHOLE_HOUSE));
    this.openItemsDataService.areas.set(areas);
    this.openItemsDataService.assertionIssues.set(assertionIssues);
    this.openItemsDataService.vendors.set(vendors);

    // Reads purely from local IndexedDB (no network); shared by the immediate cache-first
    // render below and by the refresh that runs after fetchAndUpdateConstructionManagerData.
    const loadJobIssuesData = async (shouldHydrateMedia: boolean): Promise<void> => {
      const jobs = await this.jobService.getAllCompletedJobsForLot(lotId);
      const jobIds = jobs.map(job => job.id);

      if (shouldHydrateMedia) {
        await this.imageHydrationService.hydrateByJobIds(jobIds, true);
      }

      const [actionableJobIssues, allJobIssuesForLot, userVendorContacts] = await Promise.all([
        this.jobIssueService.getLeafJobIssuesForJobs(jobIds),
        this.jobIssueService.getJobIssuesForJobs(jobIds),
        this.userVendorContactService.getActiveContactsForAllVendorsInCommunity(lot?.externalFinancialCommunityId ?? communityId)
      ]);

      const openActionableJobIssues = actionableJobIssues.filter(issue => issue.isComplete === false);

      const [media, jobIssueVendors] = await Promise.all([
        this.mediaService.getMediaForJobIssues(openActionableJobIssues),
        this.jobIssueVendorService.getAllActiveVendorsForIssues(openActionableJobIssues.map(issue => issue.identifier)),
      ]);

      this.openItemsDataService.jobs.set(jobs);
      this.openItemsDataService.actionableJobIssues.set(openActionableJobIssues);
      this.openItemsDataService.allJobIssuesForLot.set(allJobIssuesForLot);
      this.openItemsDataService.media.set(media);
      this.openItemsDataService.jobIssueVendors.set(jobIssueVendors);
      this.openItemsDataService.userVendorContacts.set(userVendorContacts);
    };

    this.openItemsDataService.isLoadingJobIssues.set(true);
    await loadJobIssuesData(false);
    const hadCachedData = (this.openItemsDataService.jobs()?.length ?? 0) > 0;

    if (hadCachedData) {
      // Something is already on screen - swap the blocking skeleton for the non-blocking indicator.
      this.openItemsDataService.isLoadingJobIssues.set(false);
      this.openItemsDataService.isRefreshingInBackground.set(true);
    }
    // Otherwise there's nothing to show yet, so isLoadingJobIssues stays true (skeleton) through the fetch below.

    try {
      const didFetch = await this.dataSyncService.fetchAndUpdateConstructionManagerData([lotId]);
      await loadJobIssuesData(true);
      if (hadCachedData && didFetch) {
        this.openItemsDataService.dataUpdatedPulse.set(true);
      }
    } catch (err) {
      this.logger.error("[OpenItems] Failed to refresh job issues from server", err);
    } finally {
      this.openItemsDataService.isLoadingJobIssues.set(false);
      this.openItemsDataService.isRefreshingInBackground.set(false);
    }
  }


  goBack(): void {
    const communityId = this.openItemsDataService.financialCommunityId();
    const lotId = this.openItemsDataService.lot()?.id;
    this.router.navigateWithPersistedState(["/cm/all-my-lots/community", communityId, "lot", lotId, "summary"]);
  }
}